import { apiClient as api } from "./httpClient";

// Avaliações v2 (modelo novo — 31/08/2026): builder estilo Google Forms com seções (2 níveis),
// perguntas com opções valoradas inline e faixas de resultado. Permissão assessment-forms
// (Somente Administrador). Tenant é o único master. Formulário aplicado é imutável (duplicar).

export interface AssessmentFormRow {
  id: string;
  title: string;
  description: string | null;
  calcTypeId: string | null;
  calcTypeName: string | null;
  allowPartialCompletion: boolean;
  active: boolean;
  /** versão na família (1 = original; "Nova versão" incrementa) */
  version: number;
  sectionsCount: number;
  questionsCount: number;
  applicationsCount: number;
  locked: boolean;
  /** quantas versões a família tem (1 = instrumento sem histórico) */
  versionsCount: number;
  /** aplicações somadas em todas as versões */
  familyApplicationsCount: number;
}

export interface AssessmentFormVersionRow {
  id: string;
  title: string;
  description: string | null;
  version: number;
  active: boolean;
  /** devolvidos intactos ao alternar o status (o update é um PUT completo) */
  calcTypeId: string | null;
  calcTypeName: string | null;
  allowPartialCompletion: boolean;
  sectionsCount: number;
  questionsCount: number;
  applicationsCount: number;
  locked: boolean;
  createdAt: string;
}

export interface AssessmentFormFamily {
  title: string;
  /** versão no ar; nulo quando a família está inteira inativa */
  activeVersionId: string | null;
  versions: AssessmentFormVersionRow[];
}

export interface AssessmentFormsListResult {
  items: AssessmentFormRow[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export type AssessmentPaginationMode = "all" | "section";

export interface AssessmentFormData {
  id: string;
  title: string;
  description: string | null;
  /** instruções de interpretação do resultado (o "manual" do instrumento) */
  interpretationGuide: string | null;
  allowPartialCompletion: boolean;
  /** all = tudo numa página; section = uma seção por vez */
  paginationMode: AssessmentPaginationMode;
  calcTypeId: string | null;
  active: boolean;
  applicationsCount: number;
  locked: boolean;
  sourceFormId: string | null;
  version: number;
  /** natureza do instrumento (anamnese, avaliação inicial, periódica, acompanhamento) */
  formTypeId: string | null;
}

/** Tipo de formulário: para que o instrumento serve. `isSystem` = veio com o produto (RN-553h). */
export interface AssessmentFormType {
  id: string;
  name: string;
  description: string | null;
  code: string | null;
  itemIndex: number;
  active: boolean;
  isSystem: boolean;
  formsUsing: number;
}

export interface AssessmentSection {
  id: string;
  title: string;
  description: string | null;
  interpretationGuide: string | null;
  parentSectionId: string | null;
  ordenation: number;
  calcTypeId: string | null;
  questionsCount: number;
}

export interface AssessmentOption {
  id: string;
  label: string;
  value: number | null;
  ordenation: number;
}

export type AssessmentQuestionType = "single_choice" | "multiple_choice" | "short_text" | "long_text";

export interface AssessmentQuestion {
  id: string;
  sectionId: string | null;
  text: string;
  description: string | null;
  questionType: string;
  required: boolean;
  allowComment: boolean;
  commentRequired: boolean;
  minChoices: number | null;
  maxChoices: number | null;
  ordenation: number;
  options: AssessmentOption[];
}

export interface AssessmentCalcType {
  id: string;
  code: string;
  name: string;
  description: string | null;
}

export interface AssessmentEditorCatalogs {
  calcTypes: AssessmentCalcType[];
  questionTypes: string[];
}

export interface AssessmentFormEditor {
  form: AssessmentFormData;
  sections: AssessmentSection[];
  questions: AssessmentQuestion[];
  catalogs: AssessmentEditorCatalogs;
}

export interface SaveAssessmentFormPayload {
  title: string;
  description: string | null;
  interpretationGuide?: string | null;
  allowPartialCompletion: boolean;
  paginationMode?: AssessmentPaginationMode;
  calcTypeId: string | null;
  active?: boolean;
  /** vazio ("") limpa o tipo; ausente não mexe */
  formTypeId?: string | null;
}

export interface SaveSectionPayload {
  formId?: string;
  title: string;
  description: string | null;
  interpretationGuide?: string | null;
  parentSectionId: string | null;
  ordenation: number;
  calcTypeId: string | null;
}

export interface OptionInput {
  id: string | null;
  label: string;
  value: number | null;
}

export interface SaveQuestionPayload {
  formId?: string;
  sectionId: string | null;
  text: string;
  description: string | null;
  questionType: string;
  required: boolean;
  allowComment: boolean;
  commentRequired: boolean;
  minChoices: number | null;
  maxChoices: number | null;
  ordenation: number;
  options: OptionInput[];
}

export const QUESTION_TYPE_LABELS: Record<string, string> = {
  single_choice: "Escolha única",
  multiple_choice: "Múltipla escolha",
  short_text: "Texto curto",
  long_text: "Texto longo",
};

/** Mensagens PT-BR por código de erro do backend (Error.code). */
export const ASSESSMENT_ERRORS: Record<string, string> = {
  "Assessment.Locked":
    "Este formulário já foi aplicado a um paciente: a estrutura é imutável. Duplique o formulário para evoluí-lo.",
  "Assessment.TitleRequired": "Informe o título.",
  "Assessment.QuestionTextRequired": "Informe o texto da pergunta.",
  "Assessment.TextTooLong": "Um dos campos excede o tamanho máximo.",
  "Assessment.DuplicateTitle": "Já existe um formulário com este título.",
  "Assessment.ParentSectionInvalid":
    "Seção mãe inválida: deve ser do mesmo formulário e o limite é de 2 níveis (mãe e filha).",
  "Assessment.SectionInUse": "A seção tem perguntas ou subseções vinculadas.",
  "Assessment.QuestionTypeInvalid": "Tipo de pergunta inválido.",
  "Assessment.OptionsNotAllowed": "Pergunta de texto não tem opções de resposta.",
  "Assessment.OptionLabelRequired": "Informe o rótulo de todas as opções.",
  "Assessment.ChoicesRangeInvalid": "Na múltipla escolha, mínimo e máximo devem ser válidos (mínimo ≤ máximo).",
  "Assessment.CalcTypeInvalid": "Tipo de resultado inválido.",
  "Assessment.PaginationModeInvalid": "Modo de apresentação inválido.",
  "Assessment.AnotherVersionActive":
    "Outra versão deste formulário está ativa. Só uma versão fica ativa por vez: inative-a antes de ativar esta.",
  "Assessment.FormNotFound": "Formulário não encontrado.",
  "Assessment.SectionNotFound": "Seção não encontrada.",
  "Assessment.QuestionNotFound": "Pergunta não encontrada.",
};

export const assessmentFormsService = {
  async list(params: {
    search?: string;
    status?: "active" | "inactive" | "all";
    pageNumber?: number;
    pageSize?: number;
  }): Promise<AssessmentFormsListResult> {
    const response = await api.get<AssessmentFormsListResult>("/assessment-forms", { params });
    return response.data;
  },

  /** Tipos de formulário da entidade, para o combo do editor. */
  async listTypes(includeInactive = false): Promise<AssessmentFormType[]> {
    const response = await api.get<AssessmentFormType[]>("/assessment-forms/types", {
      params: { includeInactive },
    });
    return response.data;
  },

  async saveType(payload: {
    entityId: string;
    id?: string | null;
    name: string;
    description?: string | null;
    itemIndex?: number;
  }): Promise<string> {
    const response = await api.post<string>("/assessment-forms/types", payload);
    return response.data;
  },

  async deactivateType(id: string): Promise<void> {
    await api.delete(`/assessment-forms/types/${id}`);
  },

  async getEditor(formId: string): Promise<AssessmentFormEditor> {
    const response = await api.get<AssessmentFormEditor>(`/assessment-forms/${formId}/editor`);
    return response.data;
  },

  async create(payload: SaveAssessmentFormPayload): Promise<string> {
    const response = await api.post<{ id: string }>("/assessment-forms", payload);
    return response.data.id;
  },

  async update(id: string, payload: SaveAssessmentFormPayload): Promise<void> {
    await api.put(`/assessment-forms/${id}`, payload);
  },

  async duplicate(id: string, newTitle: string): Promise<string> {
    const response = await api.post<{ id: string }>(`/assessment-forms/${id}/duplicate`, { newTitle });
    return response.data.id;
  },

  /** Nova versão na MESMA família (version+1). Nasce INATIVA: a troca é manual — inativar a
      anterior e então ativar esta (só uma versão ativa por família). */
  /** Histórico de versões do instrumento (aceita o id de qualquer versão). */
  async getFamily(formId: string): Promise<AssessmentFormFamily> {
    const { data } = await api.get(`/assessment-forms/${formId}/family`);
    return data;
  },

  async newVersion(id: string, newTitle: string): Promise<string> {
    const response = await api.post<{ id: string }>(`/assessment-forms/${id}/new-version`, {
      newTitle,
    });
    return response.data.id;
  },

  async createSection(payload: SaveSectionPayload): Promise<string> {
    const response = await api.post<{ id: string }>("/assessment-forms/sections", payload);
    return response.data.id;
  },

  async updateSection(id: string, payload: SaveSectionPayload): Promise<void> {
    await api.put(`/assessment-forms/sections/${id}`, payload);
  },

  async deleteSection(id: string): Promise<void> {
    await api.delete(`/assessment-forms/sections/${id}`);
  },

  async createQuestion(payload: SaveQuestionPayload): Promise<string> {
    const response = await api.post<{ id: string }>("/assessment-forms/questions", payload);
    return response.data.id;
  },

  async updateQuestion(id: string, payload: SaveQuestionPayload): Promise<void> {
    await api.put(`/assessment-forms/questions/${id}`, payload);
  },

  async deleteQuestion(id: string): Promise<void> {
    await api.delete(`/assessment-forms/questions/${id}`);
  },

  /** Copia as opções desta pergunta para as demais de escolha da mesma seção (Portage). */
  async applyOptionsToSection(questionId: string): Promise<number> {
    const response = await api.post<{ affected: number }>(
      `/assessment-forms/questions/${questionId}/apply-options-to-section`
    );
    return response.data.affected;
  },

};

export function assessmentErrorMessage(error: unknown, fallback: string): string {
  const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
  if (code && ASSESSMENT_ERRORS[code]) {
    return ASSESSMENT_ERRORS[code];
  }
  return fallback;
}
